/* hp-pill-switcher — reusable blog pill/tab switcher (ARIA tabs, panel model).
 *
 * Markup contract (pills and panels matched BY ORDER, nth pill -> nth panel):
 *   <hp-pill-switcher>
 *     <div class="hpbg-pills" role="tablist" aria-label="...">
 *       <button class="hpbg-pill active" aria-selected="true">Label</button>
 *       <button class="hpbg-pill" aria-selected="false">Label</button>
 *     </div>
 *     <div class="hpbg-panels">
 *       <div class="hpbg-panel"> ...card... </div>
 *       <div class="hpbg-panel" hidden> ...card... </div>
 *     </div>
 *   </hp-pill-switcher>
 *
 * Progressive enhancement: all panels are in the HTML (crawlable). Without JS,
 * the first panel shows (others carry `hidden` in markup) and every carrier's
 * content is still in the DOM for search/AI. The element only adds interactivity.
 *
 * Stepper extras (opt-in, used by the .hpbg-switch--stepper layout; harmless to
 * every other switcher):
 *   - Pills before the active one get an `.is-done` class (CSS renders the step
 *     as completed, e.g. a check mark). Pills at/after active have it removed.
 *   - Any element with `data-switch-nav="prev"` / `data-switch-nav="next"` inside
 *     the switcher is wired to step back/forward and is `disabled` at the ends.
 */
(function () {
  'use strict';
  class HpPillSwitcher extends HTMLElement {
    connectedCallback() {
      var pills = Array.prototype.slice.call(this.querySelectorAll('.hpbg-pill'));
      var panels = Array.prototype.slice.call(this.querySelectorAll('.hpbg-panel'));
      if (!pills.length || pills.length !== panels.length) return;

      var navPrev = Array.prototype.slice.call(this.querySelectorAll('[data-switch-nav="prev"]'));
      var navNext = Array.prototype.slice.call(this.querySelectorAll('[data-switch-nav="next"]'));

      var select = function (i) {
        pills.forEach(function (p, idx) {
          var on = idx === i;
          p.classList.toggle('active', on);
          p.classList.toggle('is-done', idx < i);
          p.setAttribute('aria-selected', on ? 'true' : 'false');
          p.tabIndex = on ? 0 : -1;
        });
        panels.forEach(function (pn, idx) { pn.hidden = idx !== i; });
        navPrev.forEach(function (b) { b.disabled = i === 0; });
        navNext.forEach(function (b) { b.disabled = i === pills.length - 1; });
      };

      var current = function () {
        var idx = pills.findIndex(function (p) { return p.classList.contains('active'); });
        return idx < 0 ? 0 : idx;
      };

      pills.forEach(function (p, i) {
        p.setAttribute('role', 'tab');
        p.addEventListener('click', function () { select(i); });
        p.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            var dir = e.key === 'ArrowRight' ? 1 : -1;
            var next = (i + dir + pills.length) % pills.length;
            pills[next].focus();
            select(next);
          }
        });
      });
      panels.forEach(function (pn) { pn.setAttribute('role', 'tabpanel'); });

      navPrev.concat(navNext).forEach(function (btn) {
        var dir = btn.getAttribute('data-switch-nav') === 'next' ? 1 : -1;
        btn.addEventListener('click', function () {
          var next = current() + dir;
          if (next >= 0 && next < pills.length) select(next);
        });
      });

      var start = pills.findIndex(function (p) { return p.getAttribute('aria-selected') === 'true'; });
      select(start >= 0 ? start : 0);
    }
  }
  if (!customElements.get('hp-pill-switcher')) {
    customElements.define('hp-pill-switcher', HpPillSwitcher);
  }
})();
